import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Lock } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSecureMutation } from '@/hooks/useSecureMutation';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const invoiceSchema = z.object({
  customer_id: z.string().min(1, 'Please select a customer'),
  invoice_date: z.string().min(1, 'Invoice date is required'),
  due_date: z.string().min(1, 'Due date is required'),
  notes: z.string().optional(),
  items: z.array(z.object({
    inventory_item_id: z.string().uuid('Must select a product'),
    quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
    unit_price: z.number().min(0, 'Unit price must be non-negative'),
  })).min(1, 'At least one item is required'),
});

type InvoiceFormData = z.infer<typeof invoiceSchema>;

interface EditInvoiceDialogProps {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditInvoiceDialog = ({ invoiceId, open, onOpenChange }: EditInvoiceDialogProps) => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, userRole } = usePermissions();
  const canEditPrice = isSuperAdmin || userRole === 'admin';

  const { data: invoice } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            id,
            inventory_item_id,
            description,
            quantity,
            unit_price,
            total_price
          )
        `)
        .eq('id', invoiceId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name')
        .eq('status', 'active')
        .order('company_name');
      
      if (error) throw error;
      return data;
    },
  });

  const { data: inventoryItems = [] } = useQuery({
    queryKey: ['inventory-items-for-invoices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, name, sku, selling_price, image_url, current_stock, committed_stock')
        .eq('type', 'finished_product')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const form = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      items: [{ inventory_item_id: '', quantity: 1, unit_price: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  // Populate form when invoice data loads
  useEffect(() => {
    if (invoice) {
      form.reset({
        customer_id: invoice.customer_id,
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        notes: invoice.notes || '',
        items: invoice.invoice_items?.map(item => ({
          inventory_item_id: item.inventory_item_id || '',
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
        })) || [{ inventory_item_id: '', quantity: 1, unit_price: 0 }],
      });
    }
  }, [invoice, form]);

  const updateInvoiceMutation = useSecureMutation(
    async (data: InvoiceFormData) => {
      // Calculate totals
      const subtotal = data.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const taxAmount = 0;
      const totalAmount = subtotal + taxAmount;

      // Update the invoice
      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({
          customer_id: data.customer_id,
          invoice_date: data.invoice_date,
          due_date: data.due_date,
          notes: data.notes,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          is_edited: true,
          last_edited_at: new Date().toISOString(),
          last_edited_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', invoiceId);

      if (invoiceError) throw invoiceError;

      // Delete old invoice items
      const { error: deleteError } = await supabase
        .from('invoice_items')
        .delete()
        .eq('invoice_id', invoiceId);

      if (deleteError) throw deleteError;

      // Create new invoice items with inventory links
      const invoiceItemsData = data.items.map(item => {
        const product = inventoryItems.find(inv => inv.id === item.inventory_item_id);
        return {
          invoice_id: invoiceId,
          inventory_item_id: item.inventory_item_id,
          description: product?.name || 'Unknown Product',
          image_url: product?.image_url,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.quantity * item.unit_price,
        };
      });

      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(invoiceItemsData);

      if (itemsError) throw itemsError;

      // Update linked order items if order exists
      const { data: orderData } = await supabase
        .from('invoices')
        .select('order_id')
        .eq('id', invoiceId)
        .single();

      if (orderData?.order_id) {
        // Delete old order items
        const { error: deleteOrderItemsError } = await supabase
          .from('order_items')
          .delete()
          .eq('order_id', orderData.order_id);

        if (deleteOrderItemsError) throw deleteOrderItemsError;

        // Create new order items
        const orderItemsData = data.items.map(item => ({
          order_id: orderData.order_id,
          inventory_item_id: item.inventory_item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.quantity * item.unit_price,
        }));

        const { error: orderItemsError } = await supabase
          .from('order_items')
          .insert(orderItemsData);

        if (orderItemsError) throw orderItemsError;

        // Update order totals
        const { error: updateOrderError } = await supabase
          .from('orders')
          .update({
            subtotal: subtotal,
            tax_amount: taxAmount,
            total_amount: totalAmount,
          })
          .eq('id', orderData.order_id);

        if (updateOrderError) throw updateOrderError;
      }

      return { invoiceId };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['invoice-edit-history', invoiceId] });
        toast.success('Invoice updated successfully');
        onOpenChange(false);
      },
      onError: (error: any) => {
        toast.error(`Failed to update invoice: ${error.message || 'Unknown error'}`);
        console.error('Error updating invoice:', error);
      },
    }
  );

  const onSubmit = (data: InvoiceFormData) => {
    updateInvoiceMutation.mutate(data);
  };

  const watchedItems = form.watch('items');
  const totalAmount = watchedItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Invoice</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="invoice_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Invoice Items</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ inventory_item_id: '', quantity: 1, unit_price: 0 })}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>

              {fields.map((field, index) => {
                const selectedProduct = inventoryItems.find(
                  inv => inv.id === watchedItems[index]?.inventory_item_id
                );
                const availableStock = selectedProduct 
                  ? (selectedProduct.current_stock - selectedProduct.committed_stock)
                  : 0;
                
                return (
                  <div key={field.id} className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5">
                        <FormField
                          control={form.control}
                          name={`items.${index}.inventory_item_id`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className={index === 0 ? '' : 'sr-only'}>Product</FormLabel>
                              <Select 
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  const product = inventoryItems.find(inv => inv.id === value);
                                  if (product) {
                                    form.setValue(`items.${index}.unit_price`, Number(product.selling_price));
                                  }
                                }} 
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select product" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {inventoryItems.map((item) => (
                                    <SelectItem key={item.id} value={item.id}>
                                      <div className="flex items-center gap-2">
                                        <span>{item.name} - ${item.selling_price}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-2">
                        <FormField
                          control={form.control}
                          name={`items.${index}.quantity`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className={index === 0 ? '' : 'sr-only'}>Quantity</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.01"
                                  placeholder="1"
                                  {...field}
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-2">
                        <FormField
                          control={form.control}
                          name={`items.${index}.unit_price`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className={index === 0 ? '' : 'sr-only'}>
                                <div className="flex items-center gap-1">
                                  Unit Price
                                  {!canEditPrice && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Lock className="h-3 w-3 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Only admins can override prices</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.01"
                                  placeholder="0.00"
                                  disabled={!canEditPrice}
                                  {...field}
                                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-2">
                        <div className={`${index === 0 ? 'pt-6' : ''} text-sm font-medium`}>
                          ${((watchedItems[index]?.quantity || 0) * (watchedItems[index]?.unit_price || 0)).toFixed(2)}
                        </div>
                      </div>
                      <div className="col-span-1">
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => remove(index)}
                            className={index === 0 ? 'mt-6' : ''}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {selectedProduct && availableStock < (watchedItems[index]?.quantity || 0) && (
                      <div className="text-xs text-amber-600 dark:text-amber-400 pl-1">
                        ⚠️ Available stock: {availableStock} (may not be sufficient)
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Invoice notes or payment terms" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateInvoiceMutation.isPending}>
                {updateInvoiceMutation.isPending ? 'Updating...' : 'Update Invoice'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
